'use client';

import React, { useState } from 'react';
import AppLayout from '@/components/AppLayout';
import { User, Zap, TrendingUp, Film, MessageCircle, CheckCircle2, Save, ExternalLink } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


export default function AccountPage() {
  const [saved, setSaved] = useState(false);
  const [displayName, setDisplayName] = useState('ContentPipeline Operator');
  const [email, setEmail] = useState('operator@contentpipeline.io');
  const [timezone, setTimezone] = useState('UTC');

  const handleSave = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  const stats = [
    { label: 'Reels Published', value: '142', icon: Film, color: 'text-violet-400', bg: 'bg-violet-400/10 border-violet-400/20' },
    { label: 'Total Views', value: '284K', icon: TrendingUp, color: 'text-emerald-400', bg: 'bg-emerald-400/10 border-emerald-400/20' },
    { label: 'DM Conversions', value: '1,847', icon: MessageCircle, color: 'text-sky-400', bg: 'bg-sky-400/10 border-sky-400/20' },
    { label: 'Scripts Generated', value: '389', icon: Zap, color: 'text-amber-400', bg: 'bg-amber-400/10 border-amber-400/20' },
  ];

  return (
    <AppLayout>
      <div className="space-y-6 fade-in">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center">
            <User size={18} className="text-primary" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-foreground">Account</h1>
            <p className="text-xs text-muted-foreground font-mono">Profile &amp; connected platforms</p>
          </div>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-4 gap-3">
          {stats?.map(({ label, value, icon: Icon, color, bg }) => (
            <div key={label} className={`flex items-center gap-3 px-4 py-3 rounded-xl border bg-card ${bg?.split(' ')?.[1]}`}>
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center border ${bg}`}>
                <Icon size={16} className={color} />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">{label}</p>
                <p className="text-xl font-semibold text-foreground font-mono">{value}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-2 gap-6">
          {/* Profile */}
          <div className="rounded-xl border border-border bg-card p-5 space-y-5">
            <div className="flex items-center gap-3 pb-4 border-b border-border">
              <div className="w-12 h-12 rounded-full bg-primary/15 border-2 border-primary/30 flex items-center justify-center">
                <User size={22} className="text-primary" />
              </div>
              <div>
                <p className="text-sm font-semibold text-foreground">{displayName}</p>
                <p className="text-xs text-muted-foreground font-mono">{email}</p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide block mb-1.5">
                  Display Name
                </label>
                <input
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e?.target?.value)}
                  className="w-full px-3 py-2 rounded-lg bg-secondary border border-border text-sm text-foreground focus:outline-none focus:border-primary/50"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide block mb-1.5">
                  Email Address
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e?.target?.value)}
                  className="w-full px-3 py-2 rounded-lg bg-secondary border border-border text-sm text-foreground focus:outline-none focus:border-primary/50"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide block mb-1.5">
                  Timezone
                </label>
                <select
                  value={timezone}
                  onChange={(e) => setTimezone(e?.target?.value)}
                  className="w-full px-3 py-2 rounded-lg bg-secondary border border-border text-sm text-foreground focus:outline-none focus:border-primary/50"
                >
                  <option value="UTC">UTC</option>
                  <option value="America/New_York">Eastern Time (ET)</option>
                  <option value="America/Los_Angeles">Pacific Time (PT)</option>
                  <option value="Europe/London">London (GMT)</option>
                  <option value="Asia/Kolkata">India (IST)</option>
                  <option value="Asia/Singapore">Singapore (SGT)</option>
                </select>
              </div>
            </div>

            <button
              onClick={handleSave}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all w-full justify-center ${
                saved
                  ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/25' :'bg-primary text-primary-foreground hover:bg-primary/90'
              }`}
            >
              {saved ? <CheckCircle2 size={14} /> : <Save size={14} />}
              {saved ? 'Saved!' : 'Save Profile'}
            </button>
          </div>

          {/* Connected Accounts */}
          <div className="space-y-4">
            <div className="rounded-xl border border-border bg-card p-5">
              <h2 className="text-sm font-semibold text-foreground mb-4 pb-3 border-b border-border">Connected Platforms</h2>
              <div className="space-y-3">
                {/* Instagram */}
                <div className="flex items-center gap-3 p-3 rounded-lg bg-secondary border border-border">
                  <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-pink-500 to-violet-600 flex items-center justify-center">
                    <Icon size={16} className="text-white" icon="instagram" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-foreground">Instagram Business</p>
                      <span className="flex items-center gap-1 text-2xs text-emerald-400 font-mono">
                        <CheckCircle2 size={10} /> Connected
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground font-mono truncate">ID: {process.env.NEXT_PUBLIC_IG_BUSINESS_ACCOUNT_ID ?? '17841426405856463'}</p>
                  </div>
                  <a
                    href="https://business.instagram.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-1.5 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <ExternalLink size={13} />
                  </a>
                </div>

                {/* Facebook */}
                <div className="flex items-center gap-3 p-3 rounded-lg bg-secondary border border-border">
                  <div className="w-9 h-9 rounded-lg bg-blue-600 flex items-center justify-center">
                    <Icon size={16} className="text-white" icon="facebook" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-foreground">Facebook Page</p>
                      <span className="flex items-center gap-1 text-2xs text-emerald-400 font-mono">
                        <CheckCircle2 size={10} /> Connected
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground font-mono truncate">Page ID: 1239096959284431</p>
                  </div>
                  <a
                    href="https://business.facebook.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-1.5 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <ExternalLink size={13} />
                  </a>
                </div>

                {/* OpenAI */}
                <div className="flex items-center gap-3 p-3 rounded-lg bg-secondary border border-border">
                  <div className="w-9 h-9 rounded-lg bg-emerald-600 flex items-center justify-center">
                    <Zap size={16} className="text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-foreground">OpenAI</p>
                      <span className="flex items-center gap-1 text-2xs text-emerald-400 font-mono">
                        <CheckCircle2 size={10} /> Connected
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground font-mono">GPT-4o · Script generation</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Plan Info */}
            <div className="rounded-xl border border-border bg-card p-5">
              <h2 className="text-sm font-semibold text-foreground mb-4 pb-3 border-b border-border">Plan &amp; Usage</h2>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Current Plan</span>
                  <span className="text-xs font-semibold text-primary px-2 py-0.5 rounded-full bg-primary/10 border border-primary/20">Pro Operator</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Reels this month</span>
                  <span className="text-xs font-mono text-foreground">142 / unlimited</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">OpenAI tokens used</span>
                  <span className="text-xs font-mono text-foreground">284,320 tokens</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Pipeline uptime</span>
                  <span className="text-xs font-mono text-emerald-400">99.7%</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
